import type { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { InsightsPage } from '../../../components/Journals';
import Layout from '../../../components/Layout';
import { toCanonicalUrl } from '../../../helpers/links';
import { imageOgStaticLoader } from '../../../helpers/og-image';
import {
  FranchiseIndex,
  FranchiseIndexStories,
  FranchisesStories,
} from '../../../types/Franchises';
import { castArray } from '../../../utils/arrays';
import {
  InsightItem,
  mapFranchiseToItem,
} from '../../../utils/journals/insights';
import { getStoryblokPage } from '../../../utils/storyblok';
import { pageTranslations } from '../../../utils/translations';

type Props = {
  locale: string;
  locales: string[];
  index: FranchiseIndex;
  indexCounts: number[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  stories: InsightItem[];
  ogImageUrl: string;
};

const Page = ({
  index,
  indexCounts,
  locale,
  locales,
  ogImageUrl,
  pagination,
  stories,
}: Props) => (
  <Layout
    canonicalUrl={toCanonicalUrl({
      path: '/insights/1',
      locale,
      hostname: String(process.env.NEXT_PUBLIC_HOSTNAME),
    })}
    description={index.content.seo?.description}
    locale={locale}
    locales={locales}
    ogImageUrl={ogImageUrl}
    title={index.content.seo?.title}
  >
    <InsightsPage
      index={index}
      indexCounts={indexCounts}
      locale={locale}
      pagination={pagination}
      stories={stories}
    />
  </Layout>
);

export const getStaticProps: GetStaticProps = async ({
  locale,
  locales,
  params,
  preview = false,
}) => {
  const translations = await pageTranslations(locale || 'en', ['insights']);
  const [number] = castArray(params?.number || '1');

  const pagination = {
    page: Number(number),
    perPage: 16,
  };

  const indexStories: FranchiseIndexStories | null = await getStoryblokPage(
    'cdn/stories',
    {
      locale,
      preview,
      starts_with: 'insights/',
      is_startpage: true,
    },
  );

  const index = indexStories?.data.stories.find(
    (s) => s.full_slug === 'insights/',
  );

  if (!index) {
    return {
      notFound: true,
    };
  }

  const indexCounts = await Promise.all(
    index.content.entrypoints.slice(0, 4).map(async ({ link }) => {
      const data = await getStoryblokPage('cdn/stories', {
        locale,
        preview,
        page: 1,
        per_page: 1,
        starts_with: link.cached_url,
        is_startpage: false,
      });
      return data?.total || 0;
    }),
  );

  const insights: FranchisesStories | null = await getStoryblokPage(
    'cdn/stories',
    {
      locale,
      preview,
      page: pagination.page,
      per_page: pagination.perPage,
      sort_by: 'content.date:desc',
      starts_with: 'insights/',
      is_startpage: false,
    },
  );

  const ogImageUrl = await imageOgStaticLoader({
    titleKey: index.content.title,
    imageSrc:
      'https://a.storyblok.com/f/119968/4000x2667/846735f337/parra-atelier-2.jpg',
    imageWidth: 1200,
    imageHeight: 630,
    overlay: true,
    locale,
  });

  return {
    props: {
      locale,
      locales,
      pagination: {
        ...pagination,
        total: insights?.total || 0,
      },
      index,
      indexCounts,
      ogImageUrl,
      stories: (insights?.data.stories || []).map(mapFranchiseToItem),
      ...translations,
    },
  };
};

export const getStaticPaths: GetStaticPaths = () => ({
  fallback: 'blocking',
  paths: [],
});

export default Page;
